<template>
  <div class="base-license-plate-search-wrapper">
    <input
      :id="name"
      v-model="licensePlate"
      type="text"
      :name="name"
      :class="{ invalid: errors[name] !== undefined }"
      :data-test-base-license-plate-input="name"
      :disabled="pending"
      max-length="20"
      @blur="submit"
      @keydown.enter="submit"
      @input="$emit('update:model-value', $event.target.value.toUpperCase().replace(/\s/g, ''))"
    /><span v-if="pending" data-test-base-license-plate-spinner class="spinner" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DataModel from './interfaces/DataModel';
import RequestService from '@/common/requestService';
import Utility from '@/common/UtilityFunctions.js';
import ApiResponseResultModel from '@/interfaces/ApiResponseResultModel';
import DmrLookupResponseModel from '@/interfaces/DmrLookupResponseModel';

export default defineComponent({
  name: 'BaseLicensePlateSearch',
  // inject: ['$validator'],
  props: {
    name: {
      type: String,
      default() {
        return 'base-license-plate-search';
      }
    },
    modelValue: {
      type: String,
      default() {
        return null;
      }
    },
    disabled: {
      type: Boolean,
      default() {
        return false;
      }
    },
    loading: {
      type: Boolean,
      default() {
        return false;
      }
    },
    minCharacterLength: {
      type: Number,
      default() {
        return 2;
      }
    },
    blockSearch: {
      type: Boolean,
      default() {
        return false;
      }
    },
    errors: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data(): DataModel {
    return {
      licensePlate: null,
      pending: false
    };
  },
  watch: {
    licensePlate(value) {
      if (value && value.length <= this.minCharacterLength) {
        this.$emit('license-plate-not-found', null);
      }
    },
    pending(value) {
      this.$emit('license-plate-pending', value);
    }
  },
  async mounted() {
    await this.$nextTick();
    this.licensePlate = this.modelValue;
    this.submit();
  },
  methods: {
    submit(): void {
      if (this.blockSearch) {
        return;
      }

      if (this.licensePlate === null) {
        return;
      }

      if (this.licensePlate.length < 3) {
        return;
      }

      this.pending = true;
      this.licensePlate = this.licensePlate.toUpperCase().replace(/\s/g, '');

      const url = encodeURI(Utility.constants().API.claimDmrLookup + this.licensePlate);

      RequestService.get<ApiResponseResultModel<DmrLookupResponseModel>>(url)
        .then((response) => {
          if (!response.data.IsSuccess) {
            Utility.logError('Lookup unsuccessful', this);
            this.pending = false;
            this.$emit('license-plate-not-found', null);
            this.$emit('license-plate-dmr-lookup-is-success', false);
            return;
          }

          if (response.data.Data) {
            const data = response.data.Data;
            const licensePlateFound = data.ExistsInDMR;

            if (licensePlateFound) {
              this.$emit('license-plate-found', data.Information);
            } else {
              this.$emit('license-plate-not-found', null);
            }

            this.$emit('license-plate-dmr-lookup-is-success', true);
          }

          this.pending = false;
        })
        .catch((err) => {
          Utility.logError(err, this);
          this.pending = false;
        });
    }
  }
});
</script>

<style lang="scss" scoped>
.base-license-plate-search-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;

  input {
    position: relative;
  }

  .spinner {
    position: absolute;
    top: 7px;
    right: 8px;
  }
}
</style>
